import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Typography,
} from "antd";
import { LockOutlined, MailOutlined } from "@ant-design/icons";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import API_CONFIG from "../config/api.js";
import { ELSA_BRAND_ASSETS } from "../config/brandAssets";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import SecureLogger from "../utils/secureLogger";
import { createLoginTransition } from "./loginTransition";
import "./Login.css";

const { Title, Text } = Typography;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [error, setError] = useState(null);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState(null);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { isDarkMode } = useTheme();
  const [form] = Form.useForm();
  const backgroundRef = useRef(null);
  const brandRef = useRef(null);
  const headlineRef = useRef(null);
  const cardRef = useRef(null);
  const mountedRef = useRef(true);
  const submittingRef = useRef(false);
  const requestRef = useRef(null);
  const transitionRef = useRef(null);
  const requestedNext = new URLSearchParams(window.location.search).get("next");
  const safeNext =
    requestedNext && requestedNext.startsWith("/payment") && !requestedNext.startsWith("//")
      ? requestedNext
      : null;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestRef.current?.abort();
      transitionRef.current?.cancel();
    };
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("session") === "expired") {
      setSessionExpiredMessage("Votre session a expiré. Veuillez vous reconnecter.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const getErrorMessage = (errorResponse) => {
    const errorData = errorResponse?.data;

    if (!errorData) {
      return "Échec de la connexion. Vérifiez vos identifiants.";
    }

    switch (errorData.code) {
      case "COMPANY_INACTIVE":
        return "Votre entreprise est inactive. Veuillez contacter le support pour réactiver votre compte.";
      case "NO_WAREHOUSE_ACCESS":
        return "Aucun établissement n'est associé à votre compte. Veuillez contacter l'administrateur pour obtenir les accès nécessaires.";
      case "ALL_WAREHOUSES_INACTIVE": {
        let message = "Tous vos établissements ou leurs entreprises sont inactifs. ";
        if (errorData.details && Array.isArray(errorData.details)) {
          message += `Détails : ${errorData.details.join(", ")}`;
        }
        message += " Veuillez contacter l'administrateur.";
        return message;
      }
      case "SINGLE_WAREHOUSE_INACTIVE":
        return (
          errorData.message ||
          "L'établissement associé à votre compte est inactif. Veuillez contacter l'administrateur."
        );
      default:
        return (
          errorData.message ||
          errorData.error ||
          "Échec de la connexion. Vérifiez vos identifiants."
        );
    }
  };

  const onFinish = async (values) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setIsTransitioning(false);
    setError(null);
    SecureLogger.debug("Login attempt started");

    try {
      const response = await axios.post(API_CONFIG.apiUrl("/login"), {
        email: values.email,
        password: values.password,
      }, { signal: controller.signal });

      if (!mountedRef.current || controller.signal.aborted) return;

      if (response.data?.token && response.data?.user) {
        SecureLogger.info("Login successful");

        if (response.data.user.hasLimitedAccess) {
          SecureLogger.warn("User has limited access to warehouses");
        }

        setIsTransitioning(true);
        transitionRef.current = createLoginTransition({
          background: backgroundRef.current,
          brand: brandRef.current,
          headline: headlineRef.current,
          card: cardRef.current,
        });
        const completed = await transitionRef.current.finished;
        if (!completed || !mountedRef.current || controller.signal.aborted) return;

        // /login redirects as soon as AuthContext becomes authenticated.
        // Commit the validated session only once the transition is complete.
        login(response.data.token, response.data.user);

        navigate(
          safeNext || (response.data.user.subscription_portal_only
            ? "/souscription"
            : "/accueil")
        );
      } else {
        SecureLogger.error("Login response missing token or user data");
        setError("Réponse invalide du serveur après la connexion.");
      }
    } catch (err) {
      if (!mountedRef.current || controller.signal.aborted || axios.isCancel(err)) return;
      transitionRef.current?.cancel();
      transitionRef.current = null;
      setIsTransitioning(false);
      SecureLogger.authError("Login", err);

      setError(getErrorMessage(err.response));
    } finally {
      submittingRef.current = false;
      if (requestRef.current === controller) requestRef.current = null;
      if (mountedRef.current) setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    SecureLogger.debug("Form validation failed", {
      fieldCount: errorInfo.errorFields?.length,
    });
  };

  return (
    <div
      className={`login-shell${isTransitioning ? " login-shell--transitioning" : ""}`}
    >
      <img
        ref={backgroundRef}
        className="login-shell__background"
        src={ELSA_BRAND_ASSETS.loginBackgroundDense}
        alt=""
        aria-hidden="true"
        draggable={false}
      />
      <div className="login-shell__veil" aria-hidden="true" />

      <main className="login-shell__content">
        <header className="login-brand-panel">
          <div ref={brandRef} className="login-brand-panel__brand">
            <img
              className="login-brand-panel__logo"
              src={isDarkMode ? ELSA_BRAND_ASSETS.logoHorizontalWhite : ELSA_BRAND_ASSETS.logoHorizontalNavy}
              alt="ELSA GESTION — Gardez le contrôle, où que vous soyez."
              width={204}
              height={74}
            />
          </div>
          <div ref={headlineRef} className="login-brand-panel__content">
            <Title level={1} className="login-brand-panel__title">
              <span>Retrouvez toute votre gestion</span>{" "}
              <span>dans un seul espace.</span>
            </Title>
          </div>
        </header>

        <div ref={cardRef} className="login-shell__card-container">
          <Card className="login-card">
            <div className="login-card__brand">
              <div className="login-card__copy">
                <Title level={2} className="login-card__title">
                  Connexion à votre espace
                </Title>
                <Text className="login-card__subtitle">
                  Utilisez vos identifiants pour accéder à votre espace de travail.
                </Text>
              </div>
            </div>

            <Form
              form={form}
              name="login"
              aria-label="Formulaire de connexion"
              aria-busy={loading}
              initialValues={{ remember: true }}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
              layout="vertical"
              requiredMark={false}
              disabled={loading}
              className="login-form"
            >
              {sessionExpiredMessage && (
                <Form.Item className="login-form__alert">
                  <Alert
                    message={sessionExpiredMessage}
                    type="warning"
                    showIcon
                    closable
                    onClose={() => setSessionExpiredMessage(null)}
                  />
                </Form.Item>
              )}

              {error && (
                <Form.Item className="login-form__alert">
                  <Alert
                    message={error}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setError(null)}
                    style={{
                      wordWrap: "break-word",
                      whiteSpace: "pre-wrap",
                    }}
                  />
                </Form.Item>
              )}

              <Form.Item
                name="email"
                label="Adresse e-mail"
                rules={[
                  { required: true, message: "Veuillez entrer votre email!" },
                  { type: "email", message: "Veuillez entrer un email valide!" },
                ]}
                className="login-form__item"
              >
                <Input
                  prefix={<MailOutlined className="login-form__icon" />}
                  placeholder="vous@entreprise.com"
                  size="large"
                  className="login-form__input"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Mot de passe"
                rules={[
                  {
                    required: true,
                    message: "Veuillez entrer votre mot de passe!",
                  },
                ]}
                className="login-form__item login-form__item--password"
              >
                <Input.Password
                  prefix={<LockOutlined className="login-form__icon" />}
                  placeholder="Votre mot de passe"
                  size="large"
                  className="login-form__input"
                />
              </Form.Item>

              <div className="login-form__options">
                <Form.Item name="remember" valuePropName="checked" noStyle>
                  <Checkbox className="login-form__checkbox">Se souvenir de moi</Checkbox>
                </Form.Item>
                <Link to="/forgot-password" className="login-form__link">
                  Mot de passe oublié ?
                </Link>
              </div>

              <Form.Item className="login-form__submit">
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={loading}
                  className="login-form__button"
                >
                  {isTransitioning ? "Ouverture..." : loading ? "Connexion..." : "Se connecter"}
                </Button>
              </Form.Item>
            </Form>

            <div className="login-card__footer">
              <Text className="login-card__footerText">
                Pas de compte ?{" "}
                <Link to="/signup" className="login-card__footerLink">
                  S'inscrire
                </Link>
              </Text>
            </div>
          </Card>
        </div>
      </main>

      <p className="login-shell__status" role="status" aria-live="polite">
        {isTransitioning ? "Connexion validée. Ouverture de votre espace..." : loading ? "Vérification de vos identifiants..." : ""}
      </p>

    </div>
  );
};

export default Login;
